/**
 * Pure helpers for the correction embed.
 *
 * Extracted from CorrectionEmbed.vue so they can be unit-tested without
 * mounting the component. The component imports from here; behavior is
 * unchanged.
 */

/**
 * Engine question types. Committed mapping (canonical going forward):
 *
 *   0 Unused — do not emit
 *   1 Single-select MCQ
 *   2 Multi-select MCQ
 *   3 Numeric fill-in
 *   4 True / False
 *   5 Matching / association
 *   6 Ordering
 *   7 Multi-choice grid              (Learnosity-only today)
 *   8 Single-choice grid             (Learnosity-only today)
 *   9 Essay / written response
 *
 * Types 1–8 are all editable in the embed today; rendering is the same
 * flat row of bubble checkboxes regardless of type. The semantic
 * difference is which selections count as legal:
 *
 *   - Single-select (1, 4, 8) — toggling a new bubble clears any prior
 *     selection (see `isMulti` below).
 *   - Multi-select (2, 3, 5, 6, 7) — any combination is allowed; the
 *     embed forwards the user's choices to the engine as-is.
 *
 * Types 0 and 9 stay read-only:
 *   - 0 should never appear in production (engine commits to "do not
 *     emit") — render read-only as a defensive fallback if it ever does.
 *   - 9 (essay) is a free-response type with no bubble grid.
 *
 * Engine quirks worth knowing:
 *   - Numeric questions (3) carry an OCR-attempted value in
 *     `marked_answer_text`; the embed doesn't currently surface that
 *     for editing — partners correct via bubble toggles.
 *   - Matching / ordering (5, 6) and grids (7, 8) come back as a flat
 *     bubble array with no row/column metadata; the embed doesn't
 *     group them visually. Functionally usable, not pretty.
 */
export const EDITABLE_TYPES = new Set([1, 2, 3, 4, 5, 6, 7, 8]);

export const READONLY_REASON = {
    0: 'This question type isn\'t editable in this widget.',
    9: 'Essay questions aren\'t editable in this widget.',
};

/**
 * Human-readable name for each engine question type. Rendered next to
 * each question's label in the correction grid so users / partners
 * can see at a glance what they're correcting.
 */
export const TYPE_NAMES = {
    0: 'Unknown',
    1: 'Single-select MCQ',
    2: 'Multi-select MCQ',
    3: 'Numeric fill-in',
    4: 'True / False',
    5: 'Matching',
    6: 'Ordering',
    7: 'Multi-choice grid',
    8: 'Single-choice grid',
    9: 'Essay',
};

/**
 * Multi-select semantics by type. Toggling a bubble on a single-select
 * question clears any prior selection (see CorrectionEmbed's
 * `toggleBubble`); multi-select questions allow any combination.
 *
 *   1 — single-select MCQ
 *   2 — multi-select MCQ
 *   3 — numeric (multiple columns, one digit per column)
 *   4 — true/false (single-select)
 *   5 — matching (one bubble per row — enforced via grid rendering)
 *   6 — ordering (one bubble per row — enforced via grid rendering)
 *   7 — multi-choice grid (multi-select, flat-rendered)
 *   8 — single-choice grid (single-select, flat-rendered)
 *
 * For grid-rendered types (3, 5, 6) the per-row/per-column single-select
 * is enforced by the grid renderer, not this flag. `isMulti` only
 * affects the flat-bubble fallback path.
 */
const MULTI_SELECT_TYPES = new Set([2, 3, 5, 6, 7]);

/**
 * Types rendered as 2D grids in CorrectionEmbed instead of a flat
 * bubble row. Each carries its own grid orientation and per-group
 * single-select semantics — see `GRID_CONFIG` for the per-type rules.
 */
export const GRID_TYPES = new Set([3, 5, 6, 7, 8]);

/**
 * Per-type grid metadata: how to render and how toggling behaves.
 *
 *   orientation 'rows'    — table with one row per group, row labels A, B, C…
 *   orientation 'columns' — side-by-side vertical stacks, no row labels
 *   perGroupSingleSelect  — clicking clears other selections in the same group
 *   permutation           — clicking also clears other selections in the same
 *                           column (ordering: each row picks a unique position,
 *                           each position used at most once). Implies a square
 *                           grid; the embed doesn't enforce that, the engine does.
 */
const GRID_CONFIG = {
    3: { orientation: 'columns', perGroupSingleSelect: true,  permutation: false },
    5: { orientation: 'rows',    perGroupSingleSelect: true,  permutation: false },
    6: { orientation: 'rows',    perGroupSingleSelect: true,  permutation: true  },
    7: { orientation: 'rows',    perGroupSingleSelect: false, permutation: false },
    8: { orientation: 'rows',    perGroupSingleSelect: true,  permutation: false },
};

/**
 * Group a flat bubble array into rows (for matching / ordering grids)
 * or columns (for the numeric digit grid). The engine emits bubbles in
 * group-major order — first bubble of each group has the lowest-numbered
 * label:
 *
 *   - matching / ordering (5, 6): groups are rows, columns labeled
 *     "1", "2", … so `text === "1"` marks each new row.
 *   - numeric (3): groups are columns, rows labeled "0", "1", … (then
 *     ".", "-", "+", "/"), so `text === "0"` marks each new column.
 *
 * If no marker is found anywhere (single-group fallback), returns one
 * group with all bubbles — caller can detect and fall back to flat.
 *
 * @param {Array} bubbles  Flat array of bubble objects with `.text` strings.
 * @param {number} questionType  Engine type number (3, 5, or 6).
 * @returns {Array<Array>}  2D array. Each inner array is one group.
 */
export function groupBubblesIntoGrid(bubbles, questionType) {
    if (!bubbles || !bubbles.length) return [];
    const marker = questionType === 3 ? '0' : '1';

    // Engine emission order for the numeric grid (type 3) flipped from
    // column-major (0,1,2,…,9, 0,1,2,…,9, …) to row-major (0,0,0,0,0,
    // 1,1,1,1,1, …). Detect row-major when the second bubble also
    // carries the marker label, count consecutive markers at the head
    // to find the column count `n`, and bucket by `i % n` so each
    // returned group is still one column.
    if (questionType === 3 && bubbles.length > 1 && String(bubbles[1].text) === marker) {
        let n = 1;
        while (n < bubbles.length && String(bubbles[n].text) === marker) n++;
        const cols = Array.from({ length: n }, () => []);
        bubbles.forEach((b, i) => cols[i % n].push(b));
        return cols;
    }

    // Column-major (type 3) / group-major (types 5, 6, …): each new
    // marker starts a new group.
    const groups = [];
    let current = null;
    for (const b of bubbles) {
        if (current === null || String(b.text) === marker) {
            current = [];
            groups.push(current);
        }
        current.push(b);
    }
    return groups;
}

/**
 * Coerce engine boolean values. The engine inconsistently returns
 * booleans as `true`/`false`, `1`/`0`, or `'true'`/`'false'`/`'1'`/`'0'`
 * — collapse all the truthy variants into a real bool.
 */
export function toBool(v) {
    if (v === true || v === 1) return true;
    if (typeof v === 'string') return v === 'true' || v === '1';
    return false;
}

/**
 * Normalize the engine's questions payload into the shape the bubble grid
 * renders against. Engine returns either an object keyed by question index
 * or an array, where each entry is itself an array of bubble objects. The
 * first bubble entry carries the question metadata (qid, qType,
 * questionText, error flags) — subsequent entries are just bubble rows.
 *
 * Output shape (per question):
 *   { qid, label, questionType, qType, isMulti,
 *     isMultiAttemptError, isPartialError,
 *     editable, readonlyReason,
 *     bubbles: [{ ansid, text, marked, metaData }] }
 */
export function normalizeQuestions(rawQuestions) {
    if (!rawQuestions) return [];
    const list = Array.isArray(rawQuestions) ? rawQuestions : Object.values(rawQuestions);

    return list.map((bubbles) => {
        const arr = Array.isArray(bubbles) ? bubbles : Object.values(bubbles || {});
        const first = arr[0] || {};
        // Preserve type 0 explicitly — only fall back to type 1 on real
        // NaN (`parseInt("")`, `parseInt(null)`, `parseInt("xyz")`).
        // The `|| 1` shortcut would silently promote 0 → 1 because 0
        // is falsy, masking the engine's "do not emit" sentinel.
        const parsedType = parseInt(first.question_type ?? first.questionType, 10);
        const questionType = Number.isNaN(parsedType) ? 1 : parsedType;
        const questionMetaData = first.question_meta_data ?? first.ansMetaData ?? '';
        const editable = EDITABLE_TYPES.has(questionType);
        const isMulti = MULTI_SELECT_TYPES.has(questionType);

        const flatBubbles = arr.map((b) => ({
            ansid: String(b.answer_id ?? b.answerId ?? ''),
            text: b.text ?? '',
            marked: toBool(b.is_attempt ?? b.isAttempt),
            metaData: b.answer_meta_data ?? b.ansMetaData ?? '',
        }));

        // Group into a 2D grid for the types whose printed sheet
        // layout has rows × columns. If grouping yields <2 groups
        // (e.g. engine ordering differs from the marker convention),
        // null out the grid and fall back to flat rendering so the
        // embed never silently swallows bubbles.
        let grid = null;
        let gridOrientation = null;
        let perGroupSingleSelect = false;
        let permutation = false;
        if (GRID_TYPES.has(questionType)) {
            const groups = groupBubblesIntoGrid(flatBubbles, questionType);
            if (groups.length >= 2) {
                grid = groups;
                gridOrientation = GRID_CONFIG[questionType].orientation;
                perGroupSingleSelect = GRID_CONFIG[questionType].perGroupSingleSelect;
                permutation = GRID_CONFIG[questionType].permutation;
            }
        }

        return {
            qid: String(first.question_id ?? first.questionId ?? ''),
            label: first.question_text ?? first.questionText ?? '',
            questionType,
            typeName: TYPE_NAMES[questionType] ?? '',
            qType: `${questionType}_${questionMetaData}`,
            questionMetaData,
            isMulti,
            // Multi-select types legitimately have multiple marked
            // bubbles — that's the point. Suppress the engine's
            // multi-attempt-error flag for those so the embed doesn't
            // render a misleading "multiple marked" warning on a valid
            // response. (Engine team confirmed this never fires from
            // /get-scanned-sheet-data today; the mask is defensive.)
            isMultiAttemptError: toBool(first.is_multi_attempt_error ?? first.isMultiAttemptError) && !isMulti,
            isPartialError: toBool(first.is_partial_error ?? first.isPartialError),
            editable,
            readonlyReason: editable
                ? ''
                : (READONLY_REASON[questionType] || 'This question type isn\'t editable in this widget.'),
            bubbles: flatBubbles,
            grid,
            gridOrientation,
            perGroupSingleSelect,
            permutation,
        };
    });
}
